import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { usePeriodDates } from '../../../../../../../../../hooks/usePeriodDates'
import { DownloadFormat } from '../../../../../../../enums/downloadFormat'
import { useSalesOverviewReport } from '../../../../../../../hooks/salesOverviewReport/useSalesOverviewReport'
import { useExportSupportedLocale } from '../../../../../../../hooks/useExportSupportedLocale'
import { useSalesReportFilters } from '../../../../../../../hooks/useSalesReportFilters'
import { ExportActionDropdownItem } from '../../../../../../../types/exportActionDropdownItem'
import { ExportFunction } from '../../../../../../../types/exportFunction'
import { downloadSalesOverviewReportFile } from '../utils/downloadSalesOverviewReportFile'
import { downloadSalesOverviewReportInvoicesArchive } from '../utils/downloadSalesOverviewReportInvoicesArchive'
import { useDownloadSalesOverviewReportPdf } from './useDownloadSalesOverviewReportPdf'

export const useSalesOverviewReportExportActions = () => {
  const { t } = useTranslation()
  const locale = useExportSupportedLocale()
  const { organization } = useUserOrganization()
  const { firstFiscalYearStart } = organization || {}

  const { dateSelection, salesOverviewReport } = useSalesOverviewReport()
  const [{ period }] = useSalesReportFilters()
  const { fromDate: startDate, toDate: endDate } = usePeriodDates(period)

  const { download: downloadPdf, isDownloading: isDownloadingPdf } = useDownloadSalesOverviewReportPdf()

  const isExportDisabled = !salesOverviewReport.length

  const exportAsPdf = useCallback(downloadPdf, [downloadPdf])

  const downloadAsXlsx = useCallback(() => {
    downloadSalesOverviewReportFile({
      dateSelection,
      endDate,
      firstFiscalYearStart,
      format: DownloadFormat.xlsx,
      locale,
      startDate,
      t,
    })
  }, [dateSelection, endDate, firstFiscalYearStart, locale, startDate, t])

  const downloadAsCsv = useCallback(() => {
    downloadSalesOverviewReportFile({
      dateSelection,
      endDate,
      firstFiscalYearStart,
      format: DownloadFormat.csv,
      locale,
      startDate,
      t,
    })
  }, [dateSelection, endDate, firstFiscalYearStart, locale, startDate, t])

  const downloadPdfInvoices = useCallback(() => {
    downloadSalesOverviewReportInvoicesArchive({ salesOverviewReport, t })
  }, [salesOverviewReport, t])

  const performSalesReportExport: Record<ExportActionDropdownItem, ExportFunction> = useMemo(
    () => ({
      downloadAsCsv,
      downloadAsXlsx,
      downloadPdfInvoices,
      exportAsPdf,
    }),
    [downloadAsCsv, downloadAsXlsx, downloadPdfInvoices, exportAsPdf],
  )

  return {
    isExportDisabled,
    isExporting: isDownloadingPdf,
    performSalesReportExport,
  }
}
